import type { Game2048Tile } from "@ziolo/shared";
import { useRef, type PointerEvent, type ReactNode } from "react";

const CELLS = Array.from({ length: 16 }, (_, i) => i);

function tileClass(tile: Game2048Tile): string {
  const tone = Math.min(tile.value, 4096);
  const classes = ["game2048-tile", `game2048-tile--v${tone}`];
  if (tile.merged) classes.push("game2048-tile--merged");
  if (tile.mergedResult) classes.push("game2048-tile--result");
  if (tile.spawned) classes.push("game2048-tile--spawned");
  if (tile.value >= 1000) classes.push("game2048-tile--small");
  else if (tile.value >= 128) classes.push("game2048-tile--mid");
  return classes.join(" ");
}

interface Game2048BoardProps {
  tiles: readonly Game2048Tile[];
  overlay?: ReactNode;
  onSwipe: (dx: number, dy: number) => void;
}

export function Game2048Board({ tiles, overlay, onSwipe }: Game2048BoardProps) {
  const origin = useRef({ x: 0, y: 0, tracking: false });

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    origin.current.x = event.clientX;
    origin.current.y = event.clientY;
    origin.current.tracking = true;
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onPointerUp(event: PointerEvent<HTMLDivElement>) {
    if (!origin.current.tracking) return;
    origin.current.tracking = false;
    onSwipe(event.clientX - origin.current.x, event.clientY - origin.current.y);
  }

  function onPointerCancel() {
    origin.current.tracking = false;
  }

  return (
    <div
      className="game2048-board"
      role="grid"
      aria-label="Plansza 2048"
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      {CELLS.map((index) => (
        <div
          key={index}
          className="game2048-cell"
          style={{
            ["--r" as string]: Math.floor(index / 4),
            ["--c" as string]: index % 4,
          }}
        />
      ))}
      {tiles.map((tile) => (
        <div
          key={tile.id}
          className={tileClass(tile)}
          style={{
            ["--r" as string]: tile.row,
            ["--c" as string]: tile.col,
          }}
          role="gridcell"
          aria-label={`${tile.value}`}
        >
          {tile.value}
        </div>
      ))}
      {overlay}
    </div>
  );
}
